import { buildExperimentEventContext } from "./context";
import { getExperimentAssignmentRevisionAt, getExperimentByKey } from "./registry";
import { getActiveExperimentContexts, rememberExperimentContext } from "./state";
import { resolveExperimentAssignment } from "./assignment";
import { fetchUserExperimentContexts } from "./subjectStore";
import {
  flushCriticalExperimentEvents,
  trackExperimentEvent,
  trackExperimentEventOnce,
} from "./eventStore";

const resolveExperimentSubject = ({
  subjectMemberId = null,
  subjectUserId = null,
  subjectSessionId = null,
  currentUserId = "",
  currentSessionId = "",
} = {}) => {
  if (subjectMemberId) {
    return {
      subjectId: String(subjectMemberId),
      subjectScope: "member",
      eventUserId: currentUserId || String(subjectMemberId),
      eventSessionId: currentSessionId || null,
    };
  }

  if (subjectUserId) {
    return {
      subjectId: String(subjectUserId),
      subjectScope: "user",
      eventUserId: String(subjectUserId),
      eventSessionId: currentSessionId || null,
    };
  }

  if (subjectSessionId) {
    return {
      subjectId: String(subjectSessionId),
      subjectScope: "session",
      eventUserId: currentUserId || null,
      eventSessionId: String(subjectSessionId),
    };
  }

  if (currentUserId) {
    return {
      subjectId: String(currentUserId),
      subjectScope: "user",
      eventUserId: String(currentUserId),
      eventSessionId: currentSessionId || null,
    };
  }

  if (currentSessionId) {
    return {
      subjectId: String(currentSessionId),
      subjectScope: "session",
      eventUserId: null,
      eventSessionId: String(currentSessionId),
    };
  }

  return {
    subjectId: "",
    subjectScope: "",
    eventUserId: null,
    eventSessionId: null,
  };
};

const DEFAULT_OUTCOME_WINDOW_DAYS = 30;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const experimentScopedOnceKey = (experimentKey = "", key = "") => {
  const normalizedExperimentKey = String(experimentKey || "").trim();
  const normalizedKey = String(key || "").trim();
  if (
    normalizedKey === normalizedExperimentKey ||
    normalizedKey.startsWith(`${normalizedExperimentKey}:`)
  ) {
    return normalizedKey;
  }
  return [normalizedExperimentKey, normalizedKey].filter(Boolean).join(":");
};

const toFiniteNumber = (value, fallback = null) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const getOutcomeWindowDays = (experiment = {}, outcomeMetric = {}) =>
  toFiniteNumber(
    outcomeMetric?.outcomeWindowDays ??
      outcomeMetric?.outcomeWindowMaxDays ??
      experiment?.outcomeWindowDays ??
      experiment?.outcomeWindowMaxDays,
    DEFAULT_OUTCOME_WINDOW_DAYS,
  );

const getOutcomeElapsedMs = (context = {}, nowMs = Date.now()) => {
  const assignedAtMs = Date.parse(context?.assignedAt || "");
  if (!Number.isFinite(assignedAtMs)) return null;
  return Math.max(0, nowMs - assignedAtMs);
};

const isOutcomeWithinWindow = ({ context = {}, windowDays = null, nowMs } = {}) => {
  const elapsedMs = getOutcomeElapsedMs(context, nowMs);
  if (elapsedMs === null) return true;
  const normalizedWindowDays = toFiniteNumber(windowDays, null);
  if (normalizedWindowDays === null || normalizedWindowDays <= 0) return true;
  return elapsedMs <= normalizedWindowDays * MS_PER_DAY;
};

const getOutcomeAssignmentProperties = ({
  activeContext = {},
  experiment = {},
  outcomeMetric = {},
  nowMs = Date.now(),
} = {}) => {
  const metadata =
    activeContext?.metadata && typeof activeContext.metadata === "object"
      ? activeContext.metadata
      : {};
  const outcomeWindowDays = getOutcomeWindowDays(experiment, outcomeMetric);
  const timeToConversionMs = getOutcomeElapsedMs(activeContext, nowMs);

  return {
    subjectId: String(activeContext?.subjectId || ""),
    subjectScope: String(activeContext?.subjectScope || ""),
    assignmentVersion: String(
      metadata.assignmentVersion || activeContext?.assignmentVersion || "",
    ),
    assignmentUnit: String(
      metadata.assignmentUnit ||
        metadata.unit ||
        activeContext?.assignmentUnit ||
        activeContext?.unit ||
        experiment?.assignment?.unit ||
        "",
    ),
    assignmentCadence: String(
      metadata.assignmentCadence ||
        metadata.cadence ||
        activeContext?.assignmentCadence ||
        activeContext?.cadence ||
        experiment?.assignment?.cadence ||
        "",
    ),
    assignmentBucketKey: String(
      metadata.assignmentBucketKey ||
        metadata.bucketKey ||
        activeContext?.assignmentBucketKey ||
        activeContext?.bucketKey ||
        "",
    ),
    outcomeWindowDays,
    timeToConversionMs,
  };
};

const mergeExperimentContextMaps = (...maps) =>
  maps.reduce((accumulator, mapValue) => {
    if (!mapValue || typeof mapValue !== "object") {
      return accumulator;
    }

    Object.entries(mapValue).forEach(([experimentKey, context]) => {
      const resolvedExperimentKey =
        String(context?.experimentKey || experimentKey || "").trim();
      if (!resolvedExperimentKey) return;

      accumulator[resolvedExperimentKey] = {
        experimentKey: resolvedExperimentKey,
        ...(context && typeof context === "object" ? context : {}),
      };
    });

    return accumulator;
  }, {});

const compactProperties = (properties = {}) =>
  Object.fromEntries(
    Object.entries(properties).filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    ),
  );

const onceScopeForSubjectScope = (subjectScope = "") =>
  subjectScope === "user" || subjectScope === "member" ? "user" : "session";

const metricSubjectOptionsForContext = ({
  activeContext = {},
  fallbackSubject = {},
} = {}) => {
  const subjectScope = String(
    activeContext?.subjectScope || fallbackSubject?.subjectScope || "",
  ).trim();
  const subjectId = String(
    activeContext?.subjectId || fallbackSubject?.subjectId || "",
  ).trim();

  if (subjectScope === "member" && subjectId) {
    return {
      subjectMemberId: subjectId,
      subjectUserId: null,
      subjectSessionId: null,
      subjectScope,
    };
  }
  if (subjectScope === "user" && subjectId) {
    return {
      subjectMemberId: null,
      subjectUserId: subjectId,
      subjectSessionId: null,
      subjectScope,
    };
  }
  if (subjectScope === "session" && subjectId) {
    return {
      subjectMemberId: null,
      subjectUserId: null,
      subjectSessionId: subjectId,
      subjectScope,
    };
  }
  return {
    subjectMemberId: null,
    subjectUserId: null,
    subjectSessionId: null,
    subjectScope: fallbackSubject?.subjectScope || "",
  };
};

const buildAssignmentMetricProperties = ({
  revisionId = null,
  assignmentVersion = null,
  assignmentUnit = null,
  assignmentCadence = null,
  assignmentBucketKey = null,
  assignmentBucket = null,
  assignmentStatus = null,
  eligibilityStatus = null,
  fallbackReason = null,
  configId = null,
  configVersion = null,
  assignmentHashVersion = null,
  assignmentOccurredAt = null,
} = {}) =>
  compactProperties({
    revisionId,
    assignmentVersion,
    assignmentUnit,
    assignmentCadence,
    assignmentBucketKey,
    assignmentBucket,
    assignmentStatus,
    eligibilityStatus,
    fallbackReason,
    configId,
    configVersion,
    assignmentHashVersion,
    assignmentOccurredAt,
  });

export const createExperimentTrackingApi = ({
  currentUserId = "",
  sessionId = "",
  utmSource = null,
  systemData = null,
  locationData = null,
} = {}) => {
  const rememberExperimentVariant = ({
    experimentKey = "",
    variantKey = "",
    source = "assignment",
    surface = "",
    subjectMemberId = null,
    subjectUserId = null,
    subjectSessionId = null,
    metadata = {},
  } = {}) => {
    const { subjectId, subjectScope } = resolveExperimentSubject({
      subjectMemberId,
      subjectUserId,
      subjectSessionId,
      currentUserId,
      currentSessionId: sessionId,
    });

    return rememberExperimentContext({
      experimentKey,
      variantKey,
      source,
      surface,
      subjectId,
      subjectScope,
      metadata,
    });
  };

  const getExperimentAssignment = (experimentKey = "", options = {}) => {
    const experiment = getExperimentAssignmentRevisionAt(
      getExperimentByKey(experimentKey),
      options.date || new Date(),
    );
    if (!experiment) return null;

    return resolveExperimentAssignment(experiment, {
      userId: currentUserId,
      sessionId,
      ...options,
    });
  };

  const trackExperimentMetric = async ({
    experimentKey = "",
    variantKey = "",
    revisionId = null,
    metricKey = "",
    metricType = "action",
    numericValue = null,
    surface = "",
    source = "observed",
    subjectMemberId = null,
    subjectUserId = null,
    subjectSessionId = null,
    assignmentVersion = null,
    assignmentUnit = null,
    assignmentCadence = null,
    assignmentBucketKey = null,
    assignmentBucket = null,
    assignmentStatus = null,
    eligibilityStatus = null,
    fallbackReason = null,
    configId = null,
    configVersion = null,
    assignmentHashVersion = null,
    assignmentOccurredAt = null,
    properties = {},
    context = {},
    once = null,
    persistVariant = false,
  } = {}) => {
    const experiment = getExperimentByKey(experimentKey);
    if (!experiment?.key || experiment.status !== "active" || !metricKey) {
      return null;
    }

    const resolvedVariantKey = String(variantKey || "").trim() || "unassigned";
    const subject = resolveExperimentSubject({
      subjectMemberId,
      subjectUserId,
      subjectSessionId,
      currentUserId,
      currentSessionId: sessionId,
    });

    if (persistVariant && resolvedVariantKey !== "unassigned") {
      rememberExperimentVariant({
        experimentKey: experiment.key,
        variantKey: resolvedVariantKey,
        source,
        surface,
        subjectMemberId,
        subjectUserId,
        subjectSessionId,
        metadata: {
          metricKey,
          metricType,
        },
      });
    }

    const payload = {
      event: "experiment_metric",
      userId: subject.eventUserId,
      sessionId: subject.eventSessionId,
      properties: {
        ...properties,
        experimentKey: experiment.key,
        variantKey: resolvedVariantKey,
        metricKey,
        metricType,
        numericValue: Number.isFinite(Number(numericValue))
          ? Number(numericValue)
          : null,
        surface: surface || "",
        source: source || "observed",
        searchableToken: experiment.searchToken,
        subjectId: subject.subjectId || "",
        subjectScope: subject.subjectScope || "",
        ...buildAssignmentMetricProperties({
          revisionId:
            revisionId ||
            experiment?.assignment?.revisionId ||
            experiment?.revisionId ||
            `${experiment.key}_r1`,
          assignmentVersion,
          assignmentUnit,
          assignmentCadence,
          assignmentBucketKey,
          assignmentBucket,
          assignmentStatus,
          eligibilityStatus,
          fallbackReason,
          configId,
          configVersion,
          assignmentHashVersion,
          assignmentOccurredAt,
        }),
      },
      context: buildExperimentEventContext({
        utmSource,
        systemData,
        locationData,
        extraContext: context,
      }),
    };

    if (once) {
      return trackExperimentEventOnce({
        ...payload,
        scope: once?.scope || "session",
        key: experimentScopedOnceKey(experiment.key, once?.key),
      });
    }

    return trackExperimentEvent(payload);
  };

  const trackExperimentOutcome = async ({
    outcomeEvent = "",
    surface = "",
    subjectMemberId = null,
    subjectUserId = null,
    subjectSessionId = null,
    properties = {},
    context = {},
    onceKey = "",
  } = {}) => {
    const resolvedOutcomeEvent = String(outcomeEvent || "").trim();
    if (!resolvedOutcomeEvent) {
      return [];
    }

    const subject = resolveExperimentSubject({
      subjectMemberId,
      subjectUserId,
      subjectSessionId,
      currentUserId,
      currentSessionId: sessionId,
    });

    const localContexts = getActiveExperimentContexts({
      userId:
        subject.subjectScope === "user"
          ? subject.subjectId
          : currentUserId || "",
      sessionId:
        subject.subjectScope === "session"
          ? subject.subjectId
          : sessionId || "",
    });
    const remoteUserContexts = await fetchUserExperimentContexts({
      userId:
        subject.subjectScope === "user"
          ? subject.subjectId
          : currentUserId || "",
    });
    const activeContexts = mergeExperimentContextMaps(
      remoteUserContexts,
      localContexts,
    );

    const nowMs = Date.now();
    const tasks = Object.values(activeContexts).flatMap((activeContext) => {
      const experiment = getExperimentByKey(activeContext?.experimentKey || "");
      if (!experiment?.key || experiment.status !== "active") {
        return [];
      }
      const outcomeMetric = (experiment?.outcomeMetrics || []).find(
        (metric) => metric?.sourceEvent === resolvedOutcomeEvent,
      );
      const variantKey = String(activeContext?.variantKey || "").trim();

      if (!outcomeMetric?.key || !variantKey) {
        return [];
      }

      const outcomeWindowDays = getOutcomeWindowDays(experiment, outcomeMetric);
      if (
        !isOutcomeWithinWindow({
          context: activeContext,
          windowDays: outcomeWindowDays,
          nowMs,
        })
      ) {
        return [];
      }

      const metricSubject = metricSubjectOptionsForContext({
        activeContext,
        fallbackSubject: subject,
      });

      return [
        trackExperimentMetric({
          experimentKey: experiment.key,
          revisionId: activeContext?.metadata?.revisionId || experiment.revisionId,
          variantKey,
          metricKey: outcomeMetric.key,
          metricType: "outcome",
          surface,
          subjectUserId: metricSubject.subjectUserId,
          subjectMemberId: metricSubject.subjectMemberId,
          subjectSessionId: metricSubject.subjectSessionId,
          properties: {
            ...properties,
            sourceEvent: resolvedOutcomeEvent,
            ...getOutcomeAssignmentProperties({
              activeContext,
              experiment,
              outcomeMetric,
              nowMs,
            }),
          },
          context,
          once: {
            scope: onceScopeForSubjectScope(metricSubject.subjectScope),
            key: [
              experiment.key,
              resolvedOutcomeEvent,
              onceKey || "default",
            ].join(":"),
          },
        }),
      ];
    });

    if (!tasks.length) {
      return [];
    }

    return Promise.allSettled(tasks);
  };

  return {
    flushCriticalExperimentEvents,
    getExperimentAssignment,
    rememberExperimentVariant,
    trackExperimentMetric,
    trackExperimentOutcome,
  };
};
